import { c as _c } from "react/compiler-runtime";
import type { DraggableAttributes } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { IcoDragAndDrop16, IcoShuffle16, IcoTrash16 } from '@onefootprint/icons';
import { IconButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
type QuestionContainerProps = {
  children: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
  /** Absent when there is nothing to delete, which drops the control. */
  onDelete?: () => void;
  hasError?: boolean;
  hasCondition?: boolean;
  dragAttributes?: DraggableAttributes;
  dragListeners?: SyntheticListenerMap;
  canEdit: boolean;
  deleteLabel?: string;
  /**
   * Draws the selection border just outside the content instead of padding it, so the preview keeps
   * the runtime's spacing. The page's buttons need this: `PageShell` stacks them 12px apart.
   */
  flush?: boolean;
};
const QuestionContainer = t0 => {
  const $ = _c(32);
  const {
    children,
    isSelected,
    onClick,
    onDelete,
    hasError: t1,
    hasCondition: t2,
    dragAttributes,
    dragListeners,
    canEdit,
    deleteLabel,
    flush: t3
  } = t0;
  const hasError = t1 === undefined ? false : t1;
  const hasCondition = t2 === undefined ? false : t2;
  const flush = t3 === undefined ? false : t3;
  const {
    t
  } = useTranslation("playbook-custom-questions");
  let t4;
  if ($[0] !== canEdit || $[1] !== children || $[2] !== deleteLabel || $[3] !== dragAttributes || $[4] !== dragListeners || $[5] !== flush || $[6] !== hasCondition || $[7] !== hasError || $[8] !== isSelected || $[9] !== onClick || $[10] !== onDelete || $[11] !== t) {
    const borderClass = cx("rounded border-2 transition-colors", {
      "border-accent": isSelected,
      "bg-accent/8": flush && isSelected,
      "border-error": hasError && !isSelected,
      "border-transparent group-hover:border-tertiary": !hasError && !isSelected
    });
    const containerClass = cx("w-full text-left relative group cursor-pointer", flush ? "block" : ["p-3", borderClass]);
    const t5 = flush && <span aria-hidden={true} className={cx("absolute -inset-1 pointer-events-none", borderClass)} />;
    let t6;
    if ($[13] !== canEdit || $[14] !== deleteLabel || $[15] !== dragAttributes || $[16] !== dragListeners || $[17] !== flush || $[18] !== isSelected || $[19] !== onDelete || $[20] !== t) {
      t6 = canEdit && (dragListeners || onDelete) && <div className={cx("absolute z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity", flush ? "top-1/2 -translate-y-1/2 right-3" : "top-2 right-2", "backdrop-blur-sm bg-primary/80 rounded border border-tertiary px-1 py-0.5", {
        "max-md:opacity-100": isSelected,
        "max-md:pointer-events-none": !isSelected
      })}>{dragListeners && <IconButton {...dragAttributes} {...dragListeners} onClick={_temp} icon={IcoDragAndDrop16} aria-label={t(_temp2)} className="size-4 max-md:size-8 max-md:touch-none hover:cursor-grab active:cursor-grabbing" tabIndex={-1} variant="ghost" size="tiny" />}{onDelete && <IconButton onClick={event_0 => {
          event_0.stopPropagation();
          onDelete();
        }} icon={IcoTrash16} aria-label={deleteLabel ?? t(_temp3)} className="size-4 max-md:size-8" tabIndex={-1} variant="ghost" size="tiny" destructive={true} />}</div>;
      $[13] = canEdit;
      $[14] = deleteLabel;
      $[15] = dragAttributes;
      $[16] = dragListeners;
      $[17] = flush;
      $[18] = isSelected;
      $[19] = onDelete;
      $[20] = t;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    let t7;
    if ($[22] !== hasCondition || $[23] !== t) {
      t7 = hasCondition && <span className="flex items-center gap-1 text-caption-1 text-tertiary mt-2"><IcoShuffle16 />{t(_temp4)}</span>;
      $[22] = hasCondition;
      $[23] = t;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    let t8;
    if ($[25] !== children || $[26] !== t5 || $[27] !== t6 || $[28] !== t7) {
      t8 = <>{t5}{t6}{children}{t7}</>;
      $[25] = children;
      $[26] = t5;
      $[27] = t6;
      $[28] = t7;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    const content = t8;
    let t9;
    if ($[30] !== onClick) {
      t9 = event_1 => {
        event_1.stopPropagation();
        onClick();
      };
      $[30] = onClick;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    t4 = <button type="button" onClick={t9} className={cx(containerClass, "transition-colors")}>{content}</button>;
    $[0] = canEdit;
    $[1] = children;
    $[2] = deleteLabel;
    $[3] = dragAttributes;
    $[4] = dragListeners;
    $[5] = flush;
    $[6] = hasCondition;
    $[7] = hasError;
    $[8] = isSelected;
    $[9] = onClick;
    $[10] = onDelete;
    $[11] = t;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};
export default QuestionContainer;
function _temp(event) {
  return event.stopPropagation();
}
function _temp2($) {
  return $.preview.dragToReorder;
}
function _temp3($_0) {
  return $_0.preview.deleteQuestion;
}
function _temp4($_1) {
  return $_1.preview.conditional;
}